import { FormEvent, useState } from 'react';

import {
    TIdVerificationPerson,
    TIdVerificationPhone
} from '@helpers/FundingRequirements/fundingRequirements.types';
import { Button, Flex, Input, Pill, Select, Stack, Text } from '@iwoca/orion';
import { FormattedMessage, useIntl } from 'react-intl';

import { getCleanedDeMobileNumber } from './getCleanedDeMobileNumber';
import { useAddPersonPhone } from './useAddPersonPhone';

const NEW_NUMBER_OPTION = 'new_number';

const getNewNumberError = ({
    isInvalid,
    isSaveError
}: {
    isInvalid: boolean;
    isSaveError: boolean;
}) => {
    if (isInvalid) {
        return (
            <FormattedMessage
                id="_.funding_requirements_v2.id_verification.new_number_invalid"
                defaultMessage="Please enter a valid German mobile number, e.g. 0151 12345678."
            />
        );
    }
    if (isSaveError) {
        return (
            <FormattedMessage
                id="_.funding_requirements_v2.id_verification.new_number_save_error"
                defaultMessage="We couldn’t save this number. Please check it and try again."
            />
        );
    }
    return undefined;
};

const PersonName = ({ person }: { person: TIdVerificationPerson }) => (
    <Text className="fs-exclude text-xl font-med text-primary-10">
        {`${person.first_name} ${person.last_name}`}
    </Text>
);

export const IdCheckPersonStatus = ({
    person
}: {
    person: TIdVerificationPerson;
}) => (
    <Flex align="center">
        <PersonName person={person} />
        {person.status === 'completed' ? (
            <Pill variant="green">
                <FormattedMessage
                    id="_.funding_requirements_v2.status.completed"
                    defaultMessage="Completed"
                />
            </Pill>
        ) : (
            <Pill variant="london">
                <FormattedMessage
                    id="_.funding_requirements_v2.id_verification.person_in_review"
                    defaultMessage="In progress"
                />
            </Pill>
        )}
    </Flex>
);

export const IdCheckPersonForm = ({
    person,
    showName,
    isSending,
    onSend
}: {
    person: TIdVerificationPerson;
    showName: boolean;
    isSending: boolean;
    onSend: (phone: TIdVerificationPhone) => void;
}) => {
    const intl = useIntl();
    const [phoneUid, setPhoneUid] = useState(
        person.phones[0]?.uid ?? NEW_NUMBER_OPTION
    );
    const [newNumber, setNewNumber] = useState('');
    const [isNewNumberInvalid, setIsNewNumberInvalid] = useState(false);
    const { addPhone, isAdding, isAddError, resetAddError } =
        useAddPersonPhone();
    const isNewNumberSelected = phoneUid === NEW_NUMBER_OPTION;
    const phone = person.phones.find(({ uid }) => uid === phoneUid);
    const hasNumberToSend =
        phone !== undefined || (isNewNumberSelected && newNumber.trim() !== '');

    const sendToNewNumber = async () => {
        const number = getCleanedDeMobileNumber(newNumber);
        if (!number) {
            setIsNewNumberInvalid(true);
            return;
        }

        try {
            onSend(await addPhone({ personUid: person.uid, number }));
        } catch {
            // Shown on the form by isAddError, and logged by the hook.
        }
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (isNewNumberSelected) {
            sendToNewNumber();
        } else if (phone) {
            onSend(phone);
        }
    };

    return (
        <Stack>
            {showName && <PersonName person={person} />}
            <Stack as="form" onSubmit={handleSubmit}>
                <Select
                    label={
                        person.is_current_user ? (
                            <FormattedMessage
                                id="_.funding_requirements_v2.id_verification.mobile_number_label"
                                defaultMessage="Your mobile number"
                            />
                        ) : (
                            <FormattedMessage
                                id="_.funding_requirements_v2.id_verification.mobile_number_label_other"
                                defaultMessage="Mobile number"
                            />
                        )
                    }
                    className="fs-exclude"
                    value={phoneUid}
                    onChange={(event) => setPhoneUid(event.target.value)}
                >
                    {person.phones.map(({ uid, number }) => (
                        <option key={uid} value={uid}>
                            {number}
                        </option>
                    ))}
                    <option value={NEW_NUMBER_OPTION}>
                        {intl.formatMessage({
                            id: '_.funding_requirements_v2.id_verification.new_number_option',
                            defaultMessage: 'Add a new number'
                        })}
                    </option>
                </Select>
                {isNewNumberSelected && (
                    <Input
                        type="tel"
                        autoComplete="tel"
                        className="fs-exclude"
                        placeholder={intl.formatMessage({
                            id: '_.funding_requirements_v2.id_verification.new_number_placeholder',
                            defaultMessage: 'Enter your new number…'
                        })}
                        value={newNumber}
                        onChange={(event) => {
                            setNewNumber(event.target.value);
                            setIsNewNumberInvalid(false);
                            resetAddError();
                        }}
                        error={getNewNumberError({
                            isInvalid: isNewNumberInvalid,
                            isSaveError: isAddError
                        })}
                    />
                )}
                <Button
                    variant="primary-rounded"
                    size="large"
                    type="submit"
                    loading={isSending || isAdding}
                    disabled={!hasNumberToSend}
                >
                    <FormattedMessage
                        id="_.funding_requirements_v2.id_verification.send_sms"
                        defaultMessage="Send SMS"
                    />
                </Button>
            </Stack>
        </Stack>
    );
};
